import { useState, useCallback, useRef, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export type CallState =
  | 'idle'
  | 'requesting'
  | 'ringing'
  | 'connecting'
  | 'connected'
  | 'ended'
  | 'error'
  | 'incoming';

interface CallConfig {
  enableVideo?: boolean;
  enableAudio?: boolean;
}

interface IncomingCall {
  callId: string;
  callerName: string;
  callerId: string;
}

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

export function useWebRTC(authToken?: string) {
  const [callState, setCallState] = useState<CallState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const callIdRef = useRef<string | null>(null);
  const remoteIdRef = useRef<string | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);

  // Initialize socket connection
  useEffect(() => {
    const socket = io(API_URL, {
      auth: { token: authToken },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      console.log('Socket connected');
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connection error:', err);
      setError('Connection failed');
    });

    // Handle incoming call (for owner)
    socket.on('call:incoming', (data: IncomingCall) => {
      setIncomingCall(data);
      setCallState('incoming');
    });

    // Handle call accepted
    socket.on('call:accepted', async (data: { callId: string }) => {
      callIdRef.current = data.callId;
      setCallState('connecting');
      await createOffer();
    });

    // Handle call rejected
    socket.on('call:rejected', () => {
      setCallState('ended');
      setError('Call was declined');
      cleanup();
    });

    // Handle call ringing (caller side)
    socket.on('call:ringing', (data: { callId: string }) => {
      callIdRef.current = data.callId;
      setCallState('ringing');
    });

    // Handle call connected (owner side after accepting)
    socket.on('call:connected', () => {
      setCallState('connecting');
    });

    // Handle WebRTC signaling
    socket.on('signal:offer', async (data: { signal: RTCSessionDescriptionInit; senderId: string; callId: string }) => {
      remoteIdRef.current = data.senderId;
      callIdRef.current = data.callId;
      await handleOffer(data.signal);
    });

    socket.on('signal:answer', async (data: { signal: RTCSessionDescriptionInit }) => {
      await handleAnswer(data.signal);
    });

    socket.on('signal:ice', async (data: { candidate: RTCIceCandidateInit }) => {
      await handleIceCandidate(data.candidate);
    });

    // Handle call ended
    socket.on('call:ended', () => {
      setCallState('ended');
      cleanup();
    });

    // Handle call error
    socket.on('call:error', (data: { message: string }) => {
      setError(data.message);
      setCallState('error');
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      cleanup();
    };
  }, [authToken]);

  const cleanup = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
    setRemoteStream(null);
    setIsScreenSharing(false);
    callIdRef.current = null;
    remoteIdRef.current = null;
  }, [localStream]);

  const initializePeerConnection = useCallback((stream: MediaStream) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current && remoteIdRef.current) {
        socketRef.current.emit('signal:ice', {
          callId: callIdRef.current,
          candidate: event.candidate,
          targetId: remoteIdRef.current,
        });
      }
    };

    pc.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
      setCallState('connected');
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        setCallState('ended');
        cleanup();
      }
    };

    // Add local tracks
    stream.getTracks().forEach(track => {
      pc.addTrack(track, stream);
    });

    peerConnectionRef.current = pc;
    return pc;
  }, [cleanup]);

  const getMediaStream = useCallback(async (config: CallConfig = { enableAudio: true, enableVideo: false }) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: config.enableAudio !== false,
        video: config.enableVideo || false,
      });
      setLocalStream(stream);
      setIsAudioEnabled(config.enableAudio !== false);
      setIsVideoEnabled(config.enableVideo || false);
      return stream;
    } catch (err) {
      console.error('Failed to get media stream:', err);
      throw new Error('Could not access microphone/camera');
    }
  }, []);

  const createOffer = useCallback(async () => {
    if (!peerConnectionRef.current || !socketRef.current || !remoteIdRef.current) return;

    try {
      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);

      socketRef.current.emit('signal:offer', {
        callId: callIdRef.current,
        signal: offer,
        targetId: remoteIdRef.current,
      });
    } catch (err) {
      console.error('Failed to create offer:', err);
      setError('Failed to establish connection');
      setCallState('error');
    }
  }, []);

  const handleOffer = useCallback(async (offer: RTCSessionDescriptionInit) => {
    if (!peerConnectionRef.current) {
      const stream = await getMediaStream();
      initializePeerConnection(stream);
    }

    if (!peerConnectionRef.current || !socketRef.current || !remoteIdRef.current) return;

    try {
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);

      socketRef.current.emit('signal:answer', {
        callId: callIdRef.current,
        signal: answer,
        targetId: remoteIdRef.current,
      });
    } catch (err) {
      console.error('Failed to handle offer:', err);
      setError('Failed to establish connection');
      setCallState('error');
    }
  }, [getMediaStream, initializePeerConnection]);

  const handleAnswer = useCallback(async (answer: RTCSessionDescriptionInit) => {
    if (!peerConnectionRef.current) return;

    try {
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (err) {
      console.error('Failed to handle answer:', err);
    }
  }, []);

  const handleIceCandidate = useCallback(async (candidate: RTCIceCandidateInit) => {
    if (!peerConnectionRef.current) return;

    try {
      await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.error('Failed to add ICE candidate:', err);
    }
  }, []);

  // Start a call (for anonymous callers from website)
  const startCall = useCallback(async (captchaToken: string, callerName?: string) => {
    if (!socketRef.current) return;

    setCallState('requesting');
    setError(null);

    try {
      const stream = await getMediaStream();
      initializePeerConnection(stream);

      socketRef.current.emit('call:initiate', {
        captchaToken,
        callerName,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start call');
      setCallState('error');
    }
  }, [getMediaStream, initializePeerConnection]);

  // Accept incoming call (for owner)
  const acceptCall = useCallback(async () => {
    if (!socketRef.current || !incomingCall) return;

    try {
      remoteIdRef.current = incomingCall.callerId;
      callIdRef.current = incomingCall.callId;

      const stream = await getMediaStream();
      initializePeerConnection(stream);

      socketRef.current.emit('call:accept', {
        callId: incomingCall.callId,
        callerId: incomingCall.callerId,
      });

      setIncomingCall(null);
      setCallState('connecting');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept call');
      setCallState('error');
    }
  }, [incomingCall, getMediaStream, initializePeerConnection]);

  // Reject incoming call (for owner)
  const rejectCall = useCallback(() => {
    if (!socketRef.current || !incomingCall) return;

    socketRef.current.emit('call:reject', {
      callId: incomingCall.callId,
      callerId: incomingCall.callerId,
    });

    setIncomingCall(null);
    setCallState('idle');
  }, [incomingCall]);

  // End the call
  const endCall = useCallback(() => {
    if (socketRef.current && remoteIdRef.current) {
      socketRef.current.emit('call:end', {
        callId: callIdRef.current,
        targetId: remoteIdRef.current,
      });
    }
    setCallState('ended');
    cleanup();
  }, [cleanup]);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsAudioEnabled(prev => !prev);
    }
  }, [localStream]);

  // Toggle video
  const toggleVideo = useCallback(async () => {
    if (!localStream || !peerConnectionRef.current) return;

    if (isVideoEnabled) {
      // Disable video
      localStream.getVideoTracks().forEach(track => {
        track.stop();
        const sender = peerConnectionRef.current?.getSenders().find(s => s.track === track);
        if (sender) {
          peerConnectionRef.current?.removeTrack(sender);
        }
      });
      setIsVideoEnabled(false);
    } else {
      // Enable video
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const videoTrack = videoStream.getVideoTracks()[0];
        localStream.addTrack(videoTrack);
        peerConnectionRef.current.addTrack(videoTrack, localStream);
        setIsVideoEnabled(true);
      } catch (err) {
        console.error('Failed to enable video:', err);
      }
    }
  }, [localStream, isVideoEnabled]);

  // Toggle screen sharing
  const toggleScreenShare = useCallback(async () => {
    if (!peerConnectionRef.current || !localStream) return;

    if (isScreenSharing) {
      // Stop screen sharing
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }
      // Restore video if it was enabled
      if (isVideoEnabled) {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const videoTrack = videoStream.getVideoTracks()[0];
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          sender.replaceTrack(videoTrack);
        }
      }
      setIsScreenSharing(false);
    } else {
      // Start screen sharing
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        screenStreamRef.current = screenStream;

        const screenTrack = screenStream.getVideoTracks()[0];
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');

        if (sender) {
          sender.replaceTrack(screenTrack);
        } else {
          peerConnectionRef.current.addTrack(screenTrack, screenStream);
        }

        // Handle when user stops sharing via browser UI
        screenTrack.onended = () => {
          setIsScreenSharing(false);
        };

        setIsScreenSharing(true);
      } catch (err) {
        console.error('Failed to share screen:', err);
      }
    }
  }, [localStream, isVideoEnabled, isScreenSharing]);

  // Check if owner is available
  const checkAvailability = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_URL}/api/call/available`);
      const data = await response.json();
      return data.available;
    } catch {
      return false;
    }
  }, []);

  return {
    callState,
    error,
    incomingCall,
    localStream,
    remoteStream,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    checkAvailability,
  };
}
